import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * GitHub file picker + importer.
 *
 * Props:
 *   open                bool — modal visibility
 *   onClose             () — fired on backdrop click / close button
 *   token               JWT bearer for API calls
 *   onImportSuccess     (projectId, projectIds?) — fired after /github/import succeeds.
 *                       Only called when `onPick` is NOT provided (auto-import mode).
 *   onPick              (file) — when provided, swaps to PICK MODE: clicking a file
 *                       fires this callback with the file metadata instead of
 *                       importing. NewProject uses this so the user can continue
 *                       through steps 4 and 5 of the wizard before the Project
 *                       is actually created. AppsMarketplace omits this prop and
 *                       gets the auto-import behaviour.
 *   extra               object spread into the import POST body when in auto-import
 *                       mode. Ignored in pick mode (parent assembles the body).
 *
 * Implementation notes:
 *   - Two views: 'repos' (browse repositories) → 'files' (browse a repo's tree).
 *   - The backend /github/repos response carries `full_name` but NOT a full
 *     `owner` object, so we split full_name when calling /github/files.
 *   - Styling matches the existing Rush drawer/modal pattern — plain divs +
 *     inline styles + the shared `set-btn` / `set-input` classes — so it looks
 *     consistent with the Sources drawer, no MUI imports.
 *   - Backend errors with structured codes (GITHUB_NOT_CONNECTED,
 *     GITHUB_REAUTH_REQUIRED) get friendly messages.
 */
const GitHubImportDialog = ({ open, onClose, token, onImportSuccess, onPick, extra }) => {
    const pickMode = typeof onPick === 'function';
    const [view, setView] = useState('repos');           // 'repos' | 'files'
    const [repos, setRepos] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [currentPath, setCurrentPath] = useState('');

    // Reset state every time the dialog opens.
    useEffect(() => {
        if (!open) return;
        setView('repos');
        setFiles([]);
        setSelectedRepo(null);
        setCurrentPath('');
        setError(null);
        fetchRepos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const fetchRepos = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/api/integrations/github/repos', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRepos(Array.isArray(res.data) ? res.data : []);
            setView('repos');
        } catch (err) {
            const data = err.response && err.response.data;
            if (data && data.code === 'GITHUB_NOT_CONNECTED') {
                setError('GitHub is not connected yet. Open Sources and authorize GitHub, then try again.');
            } else if (data && data.code === 'GITHUB_REAUTH_REQUIRED') {
                setError('GitHub access expired. Open Sources, disconnect, and reconnect.');
            } else {
                setError((data && data.msg) || 'Failed to load repositories.');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchFiles = async (repo, path = '') => {
        setLoading(true);
        setError(null);
        try {
            // The /github/repos endpoint doesn't include a nested `owner` object —
            // derive it from full_name. Octokit-shape callers used to read
            // repo.owner.login; we kept full_name to keep the response shape small.
            const owner = (repo.full_name || '').split('/')[0];
            const res = await axios.get('/api/integrations/github/files', {
                headers: { Authorization: `Bearer ${token}` },
                // Explicit empty glob so any saved config.glob doesn't filter the
                // picker view — the user is browsing freely here.
                params: { owner, repo: repo.name, path, glob: '' },
            });
            const sorted = (res.data || []).slice().sort((a, b) => {
                if (a.type === b.type) return a.name.localeCompare(b.name);
                return a.type === 'dir' ? -1 : 1;
            });
            setFiles(sorted);
            setView('files');
            if (!selectedRepo) setSelectedRepo(repo);
            setCurrentPath(path);
        } catch (err) {
            const data = err.response && err.response.data;
            setError((data && data.msg) || 'Failed to load files.');
        } finally {
            setLoading(false);
        }
    };

    const handleRepoClick = (repo) => fetchFiles(repo, '');

    const handleFileClick = (file) => {
        if (file.type === 'dir') {
            fetchFiles(selectedRepo, file.path);
            return;
        }
        // Pick mode (NewProject): hand the file metadata back to the parent and
        // close. No /github/import call here — that happens later when the
        // wizard's Submit fires.
        if (pickMode) {
            const owner = (selectedRepo.full_name || '').split('/')[0];
            onPick({
                owner,
                repo: selectedRepo.name,
                full_name: selectedRepo.full_name,
                filePath: file.path,
                fileName: file.name,
                // Pass the file size through so the caller can estimate word
                // count for the Files-added list (NewProject uses estWords()).
                size: typeof file.size === 'number' ? file.size : null,
            });
            onClose();
            return;
        }
        handleImport(file);
    };

    const handleBackToRepos = () => {
        setView('repos');
        setSelectedRepo(null);
        setCurrentPath('');
    };

    const handleImport = async (file) => {
        if (!window.confirm(`Import ${file.name}?`)) return;
        setImporting(true);
        setError(null);
        try {
            const owner = (selectedRepo.full_name || '').split('/')[0];
            const res = await axios.post('/api/integrations/github/import', {
                owner,
                repo: selectedRepo.name,
                filePath: file.path,
                downloadUrl: file.download_url,
                ...(extra || {}),
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const ids = res.data.projectIds || (res.data.projectId ? [res.data.projectId] : []);
            if (onImportSuccess) onImportSuccess(ids[0], ids);
            onClose();
        } catch (err) {
            const data = err.response && err.response.data;
            setError('Import failed. ' + ((data && (data.msg || data.detail)) || err.message));
        } finally {
            setImporting(false);
        }
    };

    if (!open) return null;

    const crumbs = currentPath ? currentPath.split('/').filter(Boolean) : [];

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
            }}
        >
            <div style={{
                background: 'var(--bg-card, #fff)', borderRadius: 14, width: 'min(720px, 94vw)',
                maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                border: '1px solid var(--line, #e5e3dd)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
                fontFamily: 'inherit',
            }}>
                {/* Header */}
                <div style={{
                    padding: '14px 18px', borderBottom: '1px solid var(--line, #e5e3dd)',
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#24292f', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 13,
                    }}>G</div>
                    <div style={{ flex: 1 }}>
                        <b style={{ fontSize: 15 }}>{pickMode ? 'Pick a file from GitHub' : 'Import from GitHub'}</b>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                            {view === 'repos'
                                ? 'Pick a repository to browse'
                                : (pickMode ? 'Click a file to add it to your project' : 'Click a file to import as a project')}
                        </div>
                    </div>
                    <button type="button" className="set-btn sm" onClick={onClose}>Close</button>
                </div>

                {/* Breadcrumbs (file view) */}
                {view === 'files' && selectedRepo && (
                    <div style={{
                        padding: '10px 18px', borderBottom: '1px solid var(--line, #e5e3dd)',
                        fontSize: 13, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
                    }}>
                        <button type="button" onClick={handleBackToRepos} style={linkBtnStyle}>Repositories</button>
                        <span style={{ color: 'var(--ink-3)' }}>/</span>
                        <button type="button" onClick={() => fetchFiles(selectedRepo, '')} style={linkBtnStyle}>
                            {selectedRepo.full_name || selectedRepo.name}
                        </button>
                        {crumbs.map((part, i) => (
                            <React.Fragment key={i}>
                                <span style={{ color: 'var(--ink-3)' }}>/</span>
                                <button
                                    type="button"
                                    onClick={() => fetchFiles(selectedRepo, crumbs.slice(0, i + 1).join('/'))}
                                    style={linkBtnStyle}
                                >{part}</button>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {/* Error banner */}
                {error && (
                    <div style={{
                        margin: '12px 18px 0', padding: '10px 12px',
                        background: '#fbe8de', border: '1px solid #d97757',
                        borderRadius: 8, fontSize: 13, color: '#8a3e1c',
                    }}>{error}</div>
                )}

                {/* Body — listing */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 10, minHeight: 320 }}>
                    {loading && <div style={{ padding: 24, color: 'var(--ink-3)', textAlign: 'center' }}>Loading…</div>}

                    {!loading && view === 'repos' && repos.length === 0 && !error && (
                        <div style={{ padding: 24, color: 'var(--ink-3)', textAlign: 'center' }}>No repositories found on this account.</div>
                    )}
                    {!loading && view === 'repos' && repos.map((repo) => (
                        <button
                            key={repo.id || repo.full_name}
                            type="button"
                            onClick={() => handleRepoClick(repo)}
                            style={rowBtnStyle}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-soft, #f6f5f1)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ fontSize: 18 }}>📁</span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {repo.full_name || repo.name}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                                    {repo.private ? 'Private' : 'Public'} · default branch <code>{repo.default_branch || 'main'}</code>
                                </div>
                            </span>
                            <span style={{ fontSize: 18, color: 'var(--ink-3)' }}>›</span>
                        </button>
                    ))}

                    {!loading && view === 'files' && files.length === 0 && !error && (
                        <div style={{ padding: 24, color: 'var(--ink-3)', textAlign: 'center' }}>This folder is empty.</div>
                    )}
                    {!loading && view === 'files' && files.map((file) => (
                        <button
                            key={file.path}
                            type="button"
                            disabled={importing}
                            onClick={() => handleFileClick(file)}
                            style={{ ...rowBtnStyle, opacity: importing ? 0.5 : 1, cursor: importing ? 'wait' : 'pointer' }}
                            onMouseEnter={(e) => { if (!importing) e.currentTarget.style.background = 'var(--bg-soft, #f6f5f1)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ fontSize: 18 }}>{file.type === 'dir' ? '📁' : '📄'}</span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                            </span>
                            {file.type === 'file' && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{pickMode ? 'click to add' : 'click to import'}</span>}
                            {file.type === 'dir' && <span style={{ fontSize: 18, color: 'var(--ink-3)' }}>›</span>}
                        </button>
                    ))}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 18px', borderTop: '1px solid var(--line, #e5e3dd)',
                    display: 'flex', alignItems: 'center', gap: 10,
                }}>
                    {importing && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Importing…</span>}
                    <div style={{ flex: 1 }} />
                    <button type="button" className="set-btn" onClick={onClose} disabled={importing}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

const linkBtnStyle = {
    background: 'transparent', border: 'none', padding: '2px 4px',
    color: 'var(--accent, #d97757)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
};

const rowBtnStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', padding: '10px 12px', marginBottom: 4,
    border: 'none', borderRadius: 8,
    background: 'transparent', cursor: 'pointer',
    textAlign: 'left', fontFamily: 'inherit', color: 'inherit',
};

export default GitHubImportDialog;
